// ⭐ 상세 모달 관련 state 및 import 제거
import React, { useEffect, useMemo, useState } from 'react';
import {
    Table,
    Button,
    Tag,
    Space,
    Input,
    Select,
    Card,
    Row,
    Col,
    Statistic,
    Progress,
    Typography,
    message,
    Tooltip,
    Checkbox,
    Modal,
    Form,
    Tabs,
} from 'antd';
import {
    PlusOutlined,
    SearchOutlined,
    EditOutlined,
    ProjectOutlined,
    RiseOutlined,
    CheckCircleOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { projectAPI, departmentAPI } from '../../services/api';
import {
    getAllocationRate,
    getAllocationsFor,
    isFullyAllocatedTo,
    formatAmount,
    formatRate,
} from '../../utils/allocation';
import { CanAccess } from '../../components/PermissionGuard';
import dayjs from 'dayjs';
import ProjectEditModal from './ProjectEditModal';  // ⭐ 새로 만들 컴포넌트

const { Title, Text } = Typography;

const ProjectListPage = () => {
    const [projects, setProjects] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filters, setFilters] = useState({
        search: '',
        status: '',
    });
    const [selectedDepartments, setSelectedDepartments] = useState([]);
    const [selectedProject, setSelectedProject] = useState(null);
    const [editModalVisible, setEditModalVisible] = useState(false);  // ⭐ 수정 모달
    const [refreshKey, setRefreshKey] = useState(0); // 강제 리렌더링용
    const [activeTabKey, setActiveTabKey] = useState('planning'); // 탭 상태 관리
    
    // 플로우 링크 모달 관련 state
    const [flowLinkModalVisible, setFlowLinkModalVisible] = useState(false);
    const [flowLinkForm] = Form.useForm();
    const [currentProjectForFlow, setCurrentProjectForFlow] = useState(null);

    const navigate = useNavigate();

    useEffect(() => {
        fetchProjects();
        fetchDepartments();
    }, []);

    useEffect(() => {
        if (departments.length > 0) {
            fetchProjects();
        }
    }, [selectedDepartments]);

    const fetchDepartments = async () => {
        try {
            const response = await departmentAPI.getAll({ is_active: 'true' });
            const deptList = response.data.departments || [];
            setDepartments(deptList);
            // 초기 로딩시 모든 부서 선택
            setSelectedDepartments(deptList.map(dept => dept.id));
        } catch (error) {
            console.error('부서 목록 조회 실패:', error);
        }
    };

    const fetchProjects = async (forceRefresh = false) => {
        setLoading(true);
        try {
            // 강제 새로고침인 경우 캐시 무시
            let requestFilters = forceRefresh 
                ? { ...filters, _t: Date.now() } // 타임스탬프 추가로 캐시 무시
                : { ...filters };
            
            // 선택된 부서 필터 추가
            if (selectedDepartments.length > 0 && selectedDepartments.length < departments.length) {
                requestFilters.department_id = selectedDepartments.join(',');
            }
            
            const response = await projectAPI.getAll(requestFilters);
            const newProjects = response.data.projects || [];
            
            console.log('프로젝트 목록 업데이트:', newProjects.length);
            
            setProjects(newProjects);
        } catch (error) {
            message.error('프로젝트 목록 조회 실패');
            console.error('Fetch projects error:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = () => {
        fetchProjects();
    };

    const handleReset = () => {
        setFilters({ search: '', status: '' });
        setSelectedDepartments(departments.map(dept => dept.id));
        setTimeout(() => fetchProjects(), 0);
    };

    // 부서 체크박스 핸들러
    const handleDepartmentChange = (departmentId, checked) => {
        if (checked) {
            setSelectedDepartments(prev => [...prev, departmentId]);
        } else {
            setSelectedDepartments(prev => prev.filter(id => id !== departmentId));
        }
    };

    const handleSelectAll = () => {
        setSelectedDepartments(departments.map(dept => dept.id));
    };

    const handleSelectNone = () => {
        setSelectedDepartments([]);
    };

    // ⭐ 수정 모달 열기
    const handleEdit = (projectId) => {
        console.log('🔍 Edit clicked, projectId:', projectId);
        setSelectedProject(projectId);
        setEditModalVisible(true);
    };

    const handleEditSuccess = () => {
        setRefreshKey(prev => prev + 1); // 목록 새로고침
    };

    // 플로우 링크 등록 모달 열기
    const handleOpenFlowLinkModal = (project) => {
        setCurrentProjectForFlow(project);
        flowLinkForm.setFieldsValue({
            flow_link: project.flow_link || ''
        });
        setFlowLinkModalVisible(true);
    };

    // 플로우 링크 등록/수정
    const handleFlowLinkSubmit = async () => {
        try {
            const values = await flowLinkForm.validateFields();
            const response = await projectAPI.updateFlowLink(currentProjectForFlow.id, {
                flow_link: values.flow_link
            });

            if (response.data.success) {
                message.success(response.data.message);
                setFlowLinkModalVisible(false);
                flowLinkForm.resetFields();
                setCurrentProjectForFlow(null);
                // 강제 새로고침
                fetchProjects(true);
            }
        } catch (error) {
            message.error(error.response?.data?.message || '플로우 링크 등록에 실패했습니다');
        }
    };

    // 플로우 링크 모달 취소
    const handleFlowLinkCancel = () => {
        setFlowLinkModalVisible(false);
        flowLinkForm.resetFields();
        setCurrentProjectForFlow(null);
    };

    // 선택된 부서 기준으로 매출 배분 정보를 계산해 프로젝트에 부착
    //  - 프로젝트의 매출 귀속부서가 모두 선택된 경우: 전체 계약금액 (배분율 100%)
    //  - 일부 부서만 선택된 경우: 선택된 부서 배분율의 합과 그 배분금액만 표시
    const visibleProjects = useMemo(() => {
        // 부서 목록 로딩 전에는 필터를 적용하지 않는다 (선택 상태가 아직 비어있음)
        if (departments.length === 0) {
            return projects.map(project => ({
                ...project,
                selected_allocation_rate: getAllocationRate(project),
                is_partial_allocation: false,
                selected_allocations: getAllocationsFor(project),
                allocated_contract_amount: parseFloat(project.contract_amount || 0),
            }));
        }

        return projects
            .map(project => {
                const selectedRate = getAllocationRate(project, selectedDepartments);
                const isPartial = !isFullyAllocatedTo(project, selectedDepartments);

                return {
                    ...project,
                    selected_allocation_rate: selectedRate,
                    is_partial_allocation: isPartial,
                    selected_allocations: getAllocationsFor(project, selectedDepartments),
                    allocated_contract_amount: parseFloat(project.contract_amount || 0) * selectedRate / 100,
                };
            })
            .filter(project => project.selected_allocation_rate > 0);
    }, [projects, selectedDepartments, departments.length]);

    // 탭별 프로젝트 필터링
    const planningProjects = visibleProjects.filter(p => p.status === 'planning');
    const activeProjects = visibleProjects.filter(p => ['in_progress', 'completed'].includes(p.status));

    // 통계 계산 (계약금액은 선택된 부서에 배분된 금액의 합)
    const stats = {
        total: visibleProjects.length,
        planning: planningProjects.length,
        active: activeProjects.length,
        total_amount: visibleProjects.reduce((sum, p) => sum + p.allocated_contract_amount, 0),
    };

    const statusOptions = [
        { label: '전체', value: '' },
        { label: '계획', value: 'planning' },
        { label: '진행중', value: 'in_progress' },
        { label: '보류', value: 'on_hold' },
        { label: '완료', value: 'completed' },
        { label: '취소', value: 'cancelled' },
    ];


    const statusMap = {
        planning: { text: '계획', color: 'default' },
        in_progress: { text: '진행중', color: 'processing' },
        on_hold: { text: '보류', color: 'warning' },
        completed: { text: '완료', color: 'success' },
        cancelled: { text: '취소', color: 'error' },
    };

    const columns = [
        {
            title: '프로젝트 코드',
            dataIndex: 'code',
            key: 'code',
            width: 120,
            sorter: (a, b) => a.code.localeCompare(b.code),
            showSorterTooltip: false,
            render: (text) => <Text strong>{text}</Text>,
        },
        {
            title: '프로젝트명',
            dataIndex: 'name',
            key: 'name',
            width: 220,
            sorter: (a, b) => a.name.localeCompare(b.name),
            showSorterTooltip: false,
            ellipsis: {
                showTitle: false,
            },
            render: (text, record) => (
                <Tooltip title={
                    <div>
                        <div><strong>{text}</strong></div>
                        {record.client_name && <div style={{ fontSize: 12 }}>{record.client_name}</div>}
                    </div>
                }>
                    <Space orientation="vertical" size={0} style={{ width: '100%' }}>
                        <Text strong ellipsis>{text}</Text>
                        <Text type="secondary" style={{ fontSize: 12 }} ellipsis>
                            {record.client_name}
                        </Text>
                    </Space>
                </Tooltip>
            ),
        },
        {
            title: '매출 귀속 부서',
            key: 'department',
            width: 180,
            render: (_, record) => {
                // 부서 필터에서 선택된 부서의 배분만 표시 (모두 선택된 경우 전체가 대상)
                const allocations = record.selected_allocations || [];

                if (allocations.length === 0) {
                    return <Tag color="default">미지정</Tag>;
                }

                const tooltipTitle = (
                    <div>
                        <div style={{ marginBottom: 4, fontWeight: 'bold' }}>부서별 매출 분할</div>
                        {allocations.map((alloc, idx) => (
                            <div key={idx}>
                                {alloc.department_name}: {formatRate(alloc.allocation_rate)}
                            </div>
                        ))}
                        {record.is_partial_allocation && (
                            <div style={{ marginTop: 4 }}>
                                선택된 부서 배분율 합계: {formatRate(record.selected_allocation_rate)}
                            </div>
                        )}
                    </div>
                );

                // allocation이 1개면 단순 표시
                if (allocations.length === 1) {
                    return (
                        <Tag color="blue">
                            {allocations[0].department_name} ({formatRate(allocations[0].allocation_rate)})
                        </Tag>
                    );
                }

                // allocation이 여러개면 툴팁으로 상세표시
                return (
                    <Tooltip title={tooltipTitle}>
                        <Space size={4} wrap>
                            {allocations.slice(0, 2).map((alloc, idx) => (
                                <Tag key={idx} color="blue" style={{ margin: '2px 0' }}>
                                    {alloc.department_name} ({formatRate(alloc.allocation_rate)})
                                </Tag>
                            ))}
                            {allocations.length > 2 && (
                                <Tag color="default">+{allocations.length - 2}</Tag>
                            )}
                        </Space>
                    </Tooltip>
                );
            },
        },
        {
            title: '상태',
            dataIndex: 'status',
            key: 'status',
            width: 80,
            sorter: (a, b) => {
                const statusOrder = { planning: 1, in_progress: 2, on_hold: 3, completed: 4, cancelled: 5 };
                return (statusOrder[a.status] || 6) - (statusOrder[b.status] || 6);
            },
            showSorterTooltip: false,
            render: (status) => {
                const statusInfo = statusMap[status] || {};
                return <Tag color={statusInfo.color}>{statusInfo.text}</Tag>;
            },
        },
        {
            title: '진행률',
            dataIndex: 'progress',
            key: 'progress',
            width: 100,
            sorter: (a, b) => (a.progress || 0) - (b.progress || 0),
            showSorterTooltip: false,
            render: (progress) => <Progress percent={progress || 0} size="small" />,
        },
        {
            title: 'PM',
            dataIndex: ['pm', 'name'],
            key: 'pm',
            width: 80,
            sorter: (a, b) => (a.pm?.name || '').localeCompare(b.pm?.name || ''),
            showSorterTooltip: false,
            ellipsis: {
                showTitle: false,
            },
            render: (name) => (
                <Tooltip title={name}>
                    <Text ellipsis>{name || '-'}</Text>
                </Tooltip>
            ),
        },
        {
            title: '계약금액',
            dataIndex: 'contract_amount',
            key: 'contract_amount',
            width: 170,
            align: 'right',
            sorter: (a, b) => (a.allocated_contract_amount || 0) - (b.allocated_contract_amount || 0),
            showSorterTooltip: false,
            // 매출 귀속부서 중 일부만 선택된 경우 전체금액과 선택 부서 배분금액/배분율을 함께 표시
            render: (amount, record) => {
                const totalAmount = parseFloat(amount || 0);

                if (!record.is_partial_allocation) {
                    return formatAmount(totalAmount);
                }

                return (
                    <Space orientation="vertical" size={0} style={{ alignItems: 'flex-end' }}>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            {formatAmount(totalAmount)}
                        </Text>
                        <Text strong>
                            ({formatAmount(record.allocated_contract_amount)} - {formatRate(record.selected_allocation_rate)})
                        </Text>
                    </Space>
                );
            },
        },
        {
            title: '기간',
            key: 'period',
            width: 160,
            sorter: (a, b) => new Date(a.start_date || 0) - new Date(b.start_date || 0),
            showSorterTooltip: false,
            render: (_, record) => {
                if (!record.start_date) return '-';
                const start = dayjs(record.start_date).format('YY.MM.DD');
                const end = record.end_date ? dayjs(record.end_date).format('YY.MM.DD') : '-';
                return `${start} ~ ${end}`;
            },
        },
        {
            title: '공정관리',
            key: 'flow_management',
            width: 120,
            align: 'center',
            render: (_, record) => {
                const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
                const isPM = record.pm?.id === currentUser.id;
                const isAdmin = currentUser.role === 'admin' || (currentUser.roles && currentUser.roles.includes('admin'));

                if (record.flow_link) {
                    return (
                        <Button
                            type="primary"
                            size="small"
                            onClick={() => window.open(record.flow_link, '_blank')}
                            style={{
                                backgroundColor: '#52c41a',
                                borderColor: '#52c41a',
                            }}
                        >
                            플로우
                        </Button>
                    );
                } else {
                    return (isPM || isAdmin) ? (
                        <Button
                            type="dashed"
                            size="small"
                            onClick={() => handleOpenFlowLinkModal(record)}
                        >
                            링크 등록
                        </Button>
                    ) : (
                        <Text type="secondary">미설정</Text>
                    );
                }
            },
        },
        {
            title: '작업',
            key: 'action',
            width: 80,
            fixed: 'right',
            render: (_, record) => {
                const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
                const isPM = record.pm?.id === currentUser.id;
                const isAdmin = currentUser.role === 'admin' || (currentUser.roles && currentUser.roles.includes('admin'));

                return (isPM || isAdmin) ? (
                    <Button
                        type="link"
                        icon={<EditOutlined />}
                        onClick={() => handleEdit(record.id)}
                        size="small"
                    >
                        수정
                    </Button>
                ) : null;
            },
        },
    ];

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Title level={2}>프로젝트 관리</Title>
                <CanAccess feature="createProject">
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => navigate('/projects/create')}
                        size="large"
                    >
                        새 프로젝트
                    </Button>
                </CanAccess>
            </div>

            {/* 통계 카드 */}
            <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
                <Col xs={24} sm={12} lg={6}>
                    <Card>
                        <Statistic
                            title="전체 프로젝트"
                            value={stats.total}
                            prefix={<ProjectOutlined />}
                            suffix="건"
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                    <Card>
                        <Statistic
                            title="수주진행사업"
                            value={stats.planning}
                            prefix={<RiseOutlined />}
                            suffix="건"
                            valueStyle={{ color: '#faad14' }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                    <Card>
                        <Statistic
                            title="진행사업"
                            value={stats.active}
                            prefix={<CheckCircleOutlined />}
                            suffix="건"
                            valueStyle={{ color: '#52c41a' }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                    <Card>
                        <Statistic
                            title="총 계약금액"
                            value={stats.total_amount}
                            precision={0}
                            suffix="원"
                        />
                    </Card>
                </Col>
            </Row>

            {/* 검색 필터 */}
            <Card style={{ marginTop: 24 }}>
                <Space wrap>
                    <Input
                        placeholder="프로젝트명 검색"
                        value={filters.search}
                        onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                        onPressEnter={handleSearch}
                        style={{ width: 250 }}
                        prefix={<SearchOutlined />}
                    />
                    <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>
                        검색
                    </Button>
                    <Button onClick={handleReset}>초기화</Button>
                </Space>
            </Card>

            {/* 부서별 필터 */}
            <Card 
                title="부서별 필터" 
                style={{ marginTop: 16 }}
                extra={
                    <Space>
                        <Button 
                            type="link" 
                            size="small"
                            onClick={handleSelectAll}
                            disabled={selectedDepartments.length === departments.length}
                        >
                            전체 선택
                        </Button>
                        <Button 
                            type="link" 
                            size="small"
                            onClick={handleSelectNone}
                            disabled={selectedDepartments.length === 0}
                        >
                            전체 해제
                        </Button>
                        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                            ({selectedDepartments.length}/{departments.length}개 선택)
                        </Typography.Text>
                        <Tooltip title="매출이 여러 부서로 분할된 프로젝트는 선택된 부서의 배분율 합계만 계약금액에 반영됩니다">
                            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                                매출 배분 기준
                            </Typography.Text>
                        </Tooltip>
                    </Space>
                }
            >
                <div style={{ 
                    display: 'flex', 
                    flexWrap: 'wrap', 
                    gap: '8px 16px',
                    alignItems: 'center'
                }}>
                    {departments.map(dept => (
                        <div key={dept.id} style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            padding: '4px 12px',
                            border: '1px solid #d9d9d9',
                            borderRadius: '6px',
                            backgroundColor: selectedDepartments.includes(dept.id) ? '#e6f7ff' : '#fafafa',
                            borderColor: selectedDepartments.includes(dept.id) ? '#1890ff' : '#d9d9d9',
                            transition: 'all 0.3s',
                            cursor: 'pointer',
                            minWidth: '80px'
                        }}>
                            <Checkbox
                                checked={selectedDepartments.includes(dept.id)}
                                onChange={(e) => handleDepartmentChange(dept.id, e.target.checked)}
                                style={{ marginRight: 6 }}
                            />
                            <span style={{ 
                                fontSize: 13,
                                fontWeight: selectedDepartments.includes(dept.id) ? 500 : 400,
                                color: selectedDepartments.includes(dept.id) ? '#1890ff' : '#666'
                            }}>
                                {dept.name}
                            </span>
                        </div>
                    ))}
                </div>
            </Card>

            {/* 프로젝트 테이블 - 탭으로 구분 */}
            <Card style={{ marginTop: 24 }}>
                <Tabs
                    activeKey={activeTabKey}
                    onChange={setActiveTabKey}
                    items={[
                        {
                            key: 'planning',
                            label: (
                                <Space>
                                    <RiseOutlined style={{ color: '#faad14' }} />
                                    수주진행사업
                                    <Tag color="orange" size="small">
                                        {planningProjects.length}
                                    </Tag>
                                </Space>
                            ),
                            children: (
                                <Table
                                    key={`planning-${refreshKey}`}
                                    columns={columns}
                                    dataSource={planningProjects}
                                    rowKey="id"
                                    loading={loading}
                                    pagination={{
                                        pageSize: 10,
                                        showTotal: (total) => `총 ${total}건`,
                                    }}
                                    scroll={{ x: 1350 }}
                                />
                            )
                        },
                        {
                            key: 'active',
                            label: (
                                <Space>
                                    <ProjectOutlined style={{ color: '#52c41a' }} />
                                    진행사업
                                    <Tag color="green" size="small">
                                        {activeProjects.length}
                                    </Tag>
                                </Space>
                            ),
                            children: (
                                <Table
                                    key={`active-${refreshKey}`}
                                    columns={columns}
                                    dataSource={activeProjects}
                                    rowKey="id"
                                    loading={loading}
                                    pagination={{
                                        pageSize: 10,
                                        showTotal: (total) => `총 ${total}건`,
                                    }}
                                    scroll={{ x: 1350 }}
                                />
                            )
                        }
                    ]}
                />
            </Card>

            {/* ⭐ 프로젝트 수정 모달 */}
            <ProjectEditModal
                projectId={selectedProject}  // ⭐ projectId로 전달
                visible={editModalVisible}
                onCancel={() => {
                    setEditModalVisible(false);
                    setSelectedProject(null);
                }}
                onSuccess={() => {
                    setEditModalVisible(false);
                    setSelectedProject(null);
                    // 기존 상태 초기화 후 강제 새로고침
                    setProjects([]); // 상태 초기화
                    setRefreshKey(prev => prev + 1); // 강제 리렌더링
                    setTimeout(() => {
                        fetchProjects(true); // 강제 새로고침
                    }, 200);
                }}
            />

            {/* 플로우 링크 등록 모달 */}
            <Modal
                title="공정관리 링크 등록"
                open={flowLinkModalVisible}
                onOk={handleFlowLinkSubmit}
                onCancel={handleFlowLinkCancel}
                okText="등록"
                cancelText="취소"
                width={600}
            >
                <Form
                    form={flowLinkForm}
                    layout="vertical"
                    style={{ marginTop: 16 }}
                >
                    <Form.Item
                        label="플로우 링크 URL"
                        name="flow_link"
                        rules={[
                            { required: true, message: '플로우 링크를 입력해주세요' },
                            { type: 'url', message: '올바른 URL 형식을 입력해주세요' }
                        ]}
                    >
                        <Input
                            placeholder="https://example.com/flow-link"
                            style={{ width: '100%' }}
                        />
                    </Form.Item>
                    {currentProjectForFlow && (
                        <div style={{ padding: '12px', backgroundColor: '#f5f5f5', borderRadius: '6px', marginTop: 8 }}>
                            <Text type="secondary" style={{ fontSize: 13 }}>
                                <strong>프로젝트:</strong> {currentProjectForFlow.code} - {currentProjectForFlow.name}
                            </Text>
                        </div>
                    )}
                </Form>
            </Modal>
        </div>
    );
};

export default ProjectListPage;